// src/api/allSeeds.js
import {
  DynamoDBClient,
  ScanCommand,
  GetItemCommand,
  UpdateItemCommand,
  QueryCommand
} from '@aws-sdk/client-dynamodb';
import getAwsConfig from '../awsconfig';
import { updateItem, listItems } from './inventory';

const TABLE = 'allSeeds';
const INVENTORY_TABLE = 'inventory';

async function makeClient() {
  const cfg = await getAwsConfig();
  return new DynamoDBClient(cfg);
}

/** Fetch all seeds */
export async function listSeeds() {
  try {
    const db = await makeClient();
    const { Items } = await db.send(new ScanCommand({ TableName: TABLE }));
    return (Items || []).map(item => ({
      id:              item.id?.S || '',
      createdDate:     item.createdDate?.S || '',
      name:            item.name?.S || 'Unnamed Product',
      description:     item.description?.S || '',
      fullDescription: item.fullDescription?.S || '',
      collection:      item.collection?.S || 'uncategorized',
      imageUrl:        item.image_url?.S || '',
      inventory:       item.inventory?.N ? Number(item.inventory.N) : 0,
      isActive:        item.isActive?.BOOL || false,
      price:           item.price?.N ? Number(item.price.N) : 0
    }));
  } catch (error) {
    console.error('Error fetching seeds:', error);
    throw new Error(`Failed to load product catalog: ${error.message}`);
  }
}

/** Fetch a single seed (if you need it) */
export async function getSeed(id, createdDate) {
  const db = await makeClient();
  const { Item } = await db.send(new GetItemCommand({
    TableName: TABLE,
    Key: {
      id:          { S: id },
      createdDate: { S: createdDate }
    }
  }));
  if (!Item) return null;
  return {
    id:              Item.id.S,
    createdDate:     Item.createdDate.S,
    name:            Item.name.S,
    description:     Item.description.S,
    fullDescription: Item.fullDescription.S,
    collection:      Item.collection.S,
    imageUrl:        Item.image_url?.S || '',
    inventory:       Item.inventory?.N ? Number(Item.inventory.N) : 0,
    isActive:        Item.isActive?.BOOL || false,
    price:           Item.price?.N ? Number(Item.price.N) : 0
  };
}

/**
 * Find inventory items that reference this seed product
 */
async function findRelatedInventoryItems(productId) {
  const itemType = productId.includes('clone') ? 'PRODUCT-CLONE' : 'PRODUCT-SEED';
  const inventoryItems = await listItems(itemType);
  return inventoryItems.filter(item => item.productRefId === productId);
}

/** Update fields on a seed */
export async function updateSeed(id, createdDate, updates) {
  const db = await makeClient();
  const now = new Date().toISOString();

  // Remove key fields
  const toUpdate = { ...updates };
  delete toUpdate.id;
  delete toUpdate.createdDate;

  const exprParts = [];
  const names = {};
  const values = { ':u': { S: now } };

  Object.entries(toUpdate).forEach(([k, v], i) => {
    const nameKey = `#f${i}`;
    const valKey = `:v${i}`;
    exprParts.push(`${nameKey} = ${valKey}`);
    names[nameKey] = k;
    if (typeof v === 'boolean')       values[valKey] = { BOOL: v };
    else if (typeof v === 'number')   values[valKey] = { N: v.toString() };
    else                               values[valKey] = { S: v.toString() };
  });

  // Always update an updatedAt timestamp
  exprParts.push('#updatedAt = :u');
  names['#updatedAt'] = 'updatedAt';

  // Update the seed in the allSeeds table
  await db.send(new UpdateItemCommand({
    TableName: TABLE,
    Key: {
      id:          { S: id },
      createdDate: { S: createdDate }
    },
    UpdateExpression: 'SET ' + exprParts.join(', '),
    ExpressionAttributeNames: names,
    ExpressionAttributeValues: values
  }));
  
  // If inventory quantity was updated, sync with inventory items
  if (updates.inventory !== undefined) {
    try {
      // Find inventory items that reference this seed
      const relatedItems = await findRelatedInventoryItems(id);
      
      if (relatedItems.length > 0) {
        for (const item of relatedItems) {
          const itemType = item.itemType;
          const itemId = item.itemId;
          
          // For seeds, positive inventory in the product listing means 
          // quantity (unpacked seeds) in the inventory table
          if (itemType === 'PRODUCT-SEED') {
            await updateItem(itemType, itemId, {
              quantity: Math.abs(updates.inventory)
            });
          }
          // For clones, the inventory in the product listing maps to
          // either quantity or packagedCount based on whether they're in progress or ready
          else if (itemType === 'PRODUCT-CLONE') {
            await updateItem(itemType, itemId, {
              quantity: Math.abs(updates.inventory)
            });
          }
        }
      }
    } catch (error) {
      console.error('Error syncing inventory:', error);
      // We don't want to fail the main update if the inventory sync fails
    }
  }
}
